import {
  DocumentNotFoundError,
  InvalidResponseError,
  ContentValidationError
} from './appErrors';

/**
 * Validates the processDocument response matches expected schema.
 * Step 1: Structure validation
 * Step 2: Content quality validation (AI output check)
 *
 * @param {Object} data - Response data from /process-document
 * @param {string} endpoint - Endpoint name for error context
 * @returns {Object} The validated data (pass-through)
 */
export const validateProcessDocumentResponse = (data, endpoint = '/process-document') => {
  // Step 1: Structure validation
  if (!data || typeof data !== 'object') {
    throw new InvalidResponseError(endpoint, 'Response data is null or not an object', {
      receivedType: typeof data
    });
  }

  if (data.error) {
    throw new InvalidResponseError(endpoint, `Server returned error: ${data.error}`, {
      serverError: data.error
    });
  }

  if (!data.interrogation_questions) {
    throw new InvalidResponseError(endpoint, 'Missing interrogation_questions field', {
      availableKeys: Object.keys(data)
    });
  }

  if (!data.interrogation_questions.qa_pairs || !Array.isArray(data.interrogation_questions.qa_pairs)) {
    throw new InvalidResponseError(endpoint, 'Missing or invalid interrogation_questions.qa_pairs', {
      hasQaPairs: !!data.interrogation_questions.qa_pairs,
      qaPairsType: typeof data.interrogation_questions.qa_pairs
    });
  }

  // Step 2: AI Content Validation
  const qaPairs = data.interrogation_questions.qa_pairs;

  if (qaPairs.length === 0) {
    throw new ContentValidationError('exam_questions', 'No exam questions generated', {
      responseKeys: Object.keys(data)
    });
  }

  const invalidPairs = qaPairs.filter(pair => !pair.question || !pair.answer);
  if (invalidPairs.length > qaPairs.length * 0.5) {
    throw new ContentValidationError('exam_questions',
      `${invalidPairs.length} of ${qaPairs.length} QA pairs are malformed (missing question or answer)`, {
        invalidCount: invalidPairs.length,
        totalCount: qaPairs.length
      });
  }

  return data;
};

/**
 * Validates the getFeedback response.
 *
 * @param {Object} data - Response data from /get-feedback
 * @param {string} endpoint - Endpoint name for error context
 * @returns {Object} The validated data (pass-through)
 */
export const validateFeedbackResponse = (data, endpoint = '/get-feedback') => {
  if (!data || typeof data !== 'object') {
    throw new InvalidResponseError(endpoint, 'Response data is null or not an object');
  }

  if (data.feedback === undefined || data.feedback === null) {
    throw new InvalidResponseError(endpoint, 'Missing feedback field in response', {
      availableKeys: Object.keys(data)
    });
  }

  if (data.score === undefined || data.score === null) {
    throw new InvalidResponseError(endpoint, 'Missing score field in response', {
      availableKeys: Object.keys(data)
    });
  }

  if (typeof data.feedback === 'string' && data.feedback.trim().length < 10) {
    throw new ContentValidationError('feedback', 'Feedback response is too short to be meaningful', {
      feedbackLength: data.feedback.length
    });
  }

  return data;
};

/**
 * Validates document data retrieved from Firestore.
 *
 * @param {Object} docData - Document data from Firestore
 * @param {string} documentId - The document ID that was queried
 * @returns {Object} The validated document data (pass-through)
 */
export const validateDocumentData = (docData, documentId) => {
  if (!docData) {
    throw new DocumentNotFoundError(documentId);
  }

  if (!docData.title && !docData.text && !docData.textStorageRef) {
    throw new InvalidResponseError('firestore/documents', 'Document has no content (title, text, or storage ref)', {
      documentId,
      availableFields: Object.keys(docData)
    });
  }

  return docData;
};
